/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { of, throwError } from "rxjs";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import { isNullOrUndefined } from "../../../utils";
import {
	GET_FUNCTIONAL_UNITS_RESPONSE,
	GET_ROLES_RESPONSE,
	GET_USER_INFO_RESPONSE,
	GET_USER_RESPONSE,
	GET_USERS_RESPONSE
} from "./users.db";

export class Users {
	public static getRoles() {
		return of(GET_ROLES_RESPONSE);
	}

	public static getUserInfo() {
		return of(GET_USER_INFO_RESPONSE);
	}

	public static getUsers(options: HttpRequestOptions) {
		const { params, pathParams } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						this.constructor.name,
						this.getUsers.name
					)
			);

		const tenantId = pathParams?.["tenantId"];

		if (!tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing tenant token parameter",
						this.constructor.name,
						this.getUsers.name
					)
			);

		const page = httpParams.get("page");
		const size = httpParams.get("size");

		if (!page || !size)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.getUsers.name
					)
			);

		const pages = GET_USERS_RESPONSE;
		const position = +page > pages.length ? pages.length - 1 : +page;

		return of(pages[position]);
	}

	public static getUser(options: HttpRequestOptions) {
		const { pathParams } = options;

		const tenantId = pathParams?.["tenantId"];
		const userId = pathParams?.["userId"];

		if (!tenantId || isNullOrUndefined(userId))
			return throwError(
				() =>
					new ApplicationError(
						"Missing required path parameters",
						this.constructor.name,
						this.getUser.name
					)
			);

		return of(GET_USER_RESPONSE);
	}

	public static addUser(options: HttpRequestOptions) {
		const { pathParams, body } = options;

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						this.constructor.name,
						this.addUser.name
					)
			);

		const tenantId = pathParams?.["tenantId"];

		if (isNullOrUndefined(tenantId))
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						this.constructor.name,
						this.addUser.name
					)
			);

		return of("User successfully created");
	}

	public static updateUser(options: HttpRequestOptions) {
		const { pathParams, body } = options;

		if (!body)
			return throwError(
				() =>
					new ApplicationError(
						"No body provided",
						this.constructor.name,
						this.updateUser.name
					)
			);

		const tenantId = pathParams?.["tenantId"];
		const userId = pathParams?.["userId"];

		if (!tenantId || isNullOrUndefined(userId))
			return throwError(
				() =>
					new ApplicationError(
						"Missing required path parameters",
						this.constructor.name,
						this.updateUser.name
					)
			);

		return of("User successfully updated");
	}

	public static deleteUser(options: HttpRequestOptions) {
		const { pathParams } = options;

		const tenantId = pathParams?.["tenantId"];
		const userId = pathParams?.["userId"];

		if (!tenantId || isNullOrUndefined(userId))
			return throwError(
				() =>
					new ApplicationError(
						"Missing required path parameters",
						this.constructor.name,
						this.deleteUser.name
					)
			);

		return of("User successfully deleted");
	}

	public static logout() {
		return of({ logoutUrl: window.location.origin });
	}

	public static getUsersFunctionalUnits(options: HttpRequestOptions) {
		const params = options.params as HttpParams;
		const cursor = params?.get("cursor");
		const size = Number(params?.get("size") ?? 20);

		const allItems = GET_FUNCTIONAL_UNITS_RESPONSE;
		const startIndex = cursor ? parseInt(atob(cursor), 10) : 0;
		const endIndex = Math.min(startIndex + size, allItems.length);
		const content = allItems.slice(startIndex, endIndex);
		const hasNext = endIndex < allItems.length;
		const nextCursor = hasNext ? btoa(String(endIndex)) : null;

		return of({
			content,
			nextCursor,
			previousCursor: null,
			hasNext,
			hasPrevious: false,
			size,
			numberOfElements: content.length,
			empty: content.length === 0
		});
	}
}
